
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import Multiselect from "vue-multiselect";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import { Response } from "@/store/responses/types";
import { ManualResetRequest } from "@/store/manual-actions/types";
import api from "@/services/api.service";
import { FeaturesList, Region } from "@/store/types";
import { get } from "lodash";
import vselect from "vselect3";
import { stringMixin } from "@/mixins";

const { isNullOrWhitespace } = stringMixin.methods;

const Eventqueue = namespace("eventqueue");
const ManualAction = namespace("manualaction");

@Component({
	mixins: [validationMixin],
	validations: {
		manualResetRequest: {
			response: {
				responseID: {
					required
				}
			},
			reason: {
				required
			}
		}
	},
	components: {
		multiselect: Multiselect,
		"v-select": vselect
	}
})
export default class ManualEventReset extends Vue {
	@Eventqueue.Mutation setManualActionShown: any;

	@ManualAction.Action setManualEventResetData: (data: ManualResetRequest) => Promise<any>;
	@Getter("getFeaturesList") featuresList: FeaturesList;

	@Prop({ default: false }) public valid: boolean;
	@Prop({ default: -1 }) public tabIndex: number;
	@Prop({ default: null }) public region: Region;
	@Prop({ default: false, type: Boolean }) canUserInteractWithPlugin: boolean;

	/**
	 * Holds the bound data for the quick reset action
	 */
	public manualResetRequest = {
		response: null,
		reason: ""
	};

	// Hold the Events / responses for the drop down on the Events Tab (if enabled)
	public events: Response[] = [];

	// Constant to hold the server type id of a Ccure server
	readonly ccureServerTypeId: number = 1234;

	// Maximum number of events to be returned
	readonly maximumEvents: number = 200;

	// If the component is under a tab, reset data when tab changes.
	@Watch("tabIndex")
	private onTabIndexChanged() {
		this.reset();
	}

	/**
	 * Resets the selected response when the user switches regions.
	 */
	@Watch("region")
	private onSelectedRegionChanged() {
		this.manualResetRequest.response = null;
		this.events = [];
	}

	// Loads the data when the component loads
	public mounted() {
		this.loadEvents(this.maximumEvents);
	}

	private get isQuickControlCamerasEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "QuickControl", "Cameras"]);
	}

	// Reset the data
	public reset() {
		this.manualResetRequest = {
			response: null,
			reason: ""
		};
	}

	// Calls the api to get just the CCure related responses from the cache
	private async loadEvents(maximumEvents: number = 0) {
		var { data } = await api.fetchCachedResponsesByServerType(
			this.ccureServerTypeId,
			maximumEvents,
			this.region?.url
		);
		this.events = data;
	}

	/**
	 * Searches for any CCure related responses that contain the given search query.
	 */
	public async asyncFindEvent(search: string, loading: (loading: boolean) => void) {
		if (isNullOrWhitespace(search)) {
			return;
		}

		loading(true);

		try {
			var { data } = await api.searchCachedResponses(search, this.ccureServerTypeId, this.region?.url);
			this.events = data;
		} catch (err) {
			this.$notify({
				type: "error",
				title: "CCure Event Reset",
				text: "Unable to search for available CCure events, please try again."
			});
		}

		loading(false);
	}

	// Manually handles the data validation - called when the UI elements are updated
	public checkDataValid() {
		this.$v.$touch();
		let dataValid: boolean = !this.$v.$invalid;
		this.$emit("update:valid", dataValid);

		if (dataValid) {
			let resetData: ManualResetRequest = {
				responseId: this.manualResetRequest.response.responseID,
				groupId: this.manualResetRequest.response.groupID,
				serverId: this.manualResetRequest.response.serverID,
				cCureEventID: this.manualResetRequest.response.input1,
				reason: this.manualResetRequest.reason
			};

			this.setManualEventResetData(resetData);
		}
	}

	public responseSelected(response: Response) {
		this.manualResetRequest.response = response;

		this.checkDataValid();

		if (this.isQuickControlCamerasEnabled) {
			this.$emit("response-selected", response);
		}
	}
}
